import CashPayoutHoldNotice from '@/components/CashPayoutHoldNotice';
import type {DriverCashPayoutHold} from '../../../../lib/cash-handling/shared';
import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { useToast } from "@/hooks/use-toast";
import { CheckCircle, XCircle, Banknote, Clock, RefreshCw } from "lucide-react";

function pence(p: number) { return `£${(p / 100).toFixed(2)}`; }
function fmtDate(iso: string | null) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

type FullBankDetails = {
  bankAccountName: string | null;
  bankSortCode: string | null;
  bankAccountNumber: string | null;
};

type WithdrawalRequest = {
  cashHold?:DriverCashPayoutHold;
  id: number;
  driverId: number;
  driverName: string | null;
  amountPence: number;
  status: string;
  paymentReference?: string|null;
  paymentEvidence?: string;
  adminNote: string | null;
  bankSnapshot: string | null;
  fullBankDetails: FullBankDetails;
  requestedAt: string;
  processedAt: string | null;
};

type StatusFilter = "pending" | "approved" | "rejected" | "completed" | "all";

const STATUS_FILTERS: StatusFilter[] = ["pending", "approved", "rejected", "completed", "all"];

const STATUS_COLORS: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
  approved: "bg-blue-100 text-blue-800 border-blue-200",
  completed: "bg-green-100 text-green-800 border-green-200",
  rejected: "bg-red-100 text-red-800 border-red-200",
};

export default function AdminDriverPayouts() {
  useAuth();
  const { toast } = useToast();
  const token = localStorage.getItem("ayrshire_token");

  const [requests, setRequests] = useState<WithdrawalRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("pending");
  const [processingId, setProcessingId] = useState<number | null>(null);
  const [paymentReferences,setPaymentReferences]=useState<Record<number,string>>({});
  const [noteInputs, setNoteInputs] = useState<Record<number, string>>({});

  async function fetchRequests(filter: StatusFilter = statusFilter) {
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/drivers/withdrawal-requests?status=${filter}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error("Failed");
      const data = await res.json() as { requests: WithdrawalRequest[] };
      setRequests(data.requests ?? []);
    } catch {
      toast({ title: "Error", description: "Could not load payout requests", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void fetchRequests(statusFilter); }, [statusFilter]);

  async function handleAction(id: number, status: "approved" | "rejected" | "completed") {
    if(status!=="rejected"&&requests.find(r=>r.id===id)?.cashHold?.held){toast({title:"Payout on hold",description:"Resolve this driver’s cash records before sending any money.",variant:"destructive"});return;}
    const adminNote = noteInputs[id] ?? "";
    const paymentReference=status==='completed'?(paymentReferences[id]??'').trim():null;
    if(status==='completed'&&(!paymentReference||paymentReference.length<3)){toast({title:'Enter the payment reference',description:'Record the transfer reference after sending the money.',variant:'destructive'});return;}
    const labels: Record<string, string> = { approved: "Approve", rejected: "Reject", completed: "Record transfer sent for" };
    if (!confirm(`${labels[status]} this payout request?`)) return;

    setProcessingId(id);
    try {
      const terms={status,adminNote,paymentReference},storageKey='driver-withdrawal-decision:'+id+':'+JSON.stringify(terms);
      let adjustmentKey=sessionStorage.getItem(storageKey);if(!adjustmentKey){adjustmentKey=crypto.randomUUID();sessionStorage.setItem(storageKey,adjustmentKey);}
      const res = await fetch(`/api/admin/drivers/withdrawal-requests/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({...terms,adjustmentKey}),
      });
      if (!res.ok) {
        const d = await res.json() as { error?: string };
        toast({ title: "Error", description: d.error ?? "Could not process request", variant: "destructive" });
        return;
      }
      sessionStorage.removeItem(storageKey);
      toast({ title: status === "completed" ? "Transfer recorded as sent" : status === "approved" ? "Approved" : "Rejected", description: "Driver has been notified." });
      void fetchRequests(statusFilter);
    } catch {
      toast({ title: "Network error", description: "Could not connect. Try again.", variant: "destructive" });
    } finally {
      setProcessingId(null);
    }
  }

  const pendingCount = requests.filter(r => r.status === "pending").length;

  return (
    <AdminLayout current="/admin/driver-payouts">
      <div className="max-w-3xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-foreground flex items-center gap-2">
              <Banknote size={24} className="text-[#1e3a5f]" />
              Driver Payouts
            </h1>
            <p className="text-sm text-gray-500 mt-1">Review and process driver withdrawal requests. Transfer funds manually, then record the payment reference. Bank arrival remains unverified.</p>
          </div>
          <button
            onClick={() => void fetchRequests(statusFilter)}
            className="flex items-center gap-1.5 px-3 py-2 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <RefreshCw size={14} /> Refresh
          </button>
        </div>

        {/* Filter tabs */}
        <div className="flex gap-2 border-b border-gray-200 pb-0 overflow-x-auto">
          {STATUS_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setStatusFilter(f)}
              className={`px-4 py-2 text-sm font-medium rounded-t-lg border-b-2 transition-colors capitalize whitespace-nowrap ${
                statusFilter === f
                  ? "border-[#1e3a5f] text-[#1e3a5f] bg-blue-50"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50"
              }`}
            >
              {f==='completed'?'Reported sent':f}
              {f === "pending" && pendingCount > 0 && statusFilter !== "pending" && (
                <span className="ml-1.5 bg-red-500 text-white text-xs rounded-full px-1.5 py-0.5">{pendingCount}</span>
              )}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-16 text-gray-400 text-sm">Loading…</div>
        ) : requests.length === 0 ? (
          <div className="text-center py-16 text-gray-400">
            <Clock size={40} className="mx-auto mb-3 opacity-30" />
            <p className="text-sm">No {statusFilter} requests</p>
          </div>
        ) : (
          <div className="space-y-4">
            {requests.map((r) => {
              const bank = r.fullBankDetails;
              const isPending = r.status === "pending";
              const isApproved = r.status === "approved";

              return (
                <div key={r.id} className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm">
                  {/* Card header */}
                  <div className="flex items-start justify-between gap-4 p-5">
                    <div>
                      <div className="font-semibold text-gray-900 text-base">{r.driverName ?? `Driver #${r.driverId}`}</div>
                      <div className="text-xs text-gray-400 mt-0.5">Requested {fmtDate(r.requestedAt)}</div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <div className="text-2xl font-bold text-[#1e3a5f]">{pence(r.amountPence)}</div>
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full border capitalize ${STATUS_COLORS[r.status] ?? "bg-gray-100 text-gray-700 border-gray-200"}`}>
                        {r.status==='completed'?'Reported sent':r.status}
                      </span>
                    </div>
                  </div>

                  <CashPayoutHoldNotice hold={r.cashHold} driverId={r.driverId}/>
                  {/* Bank details */}
                  <div className="border-t border-gray-100 bg-gray-50 px-5 py-4">
                    <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Bank Details</div>
                    {bank.bankAccountName || bank.bankSortCode || bank.bankAccountNumber ? (
                      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 text-sm">
                        <div>
                          <div className="text-xs text-gray-400 mb-0.5">Account Name</div>
                          <div className="font-semibold text-gray-800">{bank.bankAccountName ?? "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 mb-0.5">Sort Code</div>
                          <div className="font-mono font-semibold text-gray-800">{bank.bankSortCode ?? "—"}</div>
                        </div>
                        <div>
                          <div className="text-xs text-gray-400 mb-0.5">Account Number</div>
                          <div className="font-mono font-semibold text-gray-800">{bank.bankAccountNumber ?? "—"}</div>
                        </div>
                      </div>
                    ) : (
                      <div className="text-sm text-gray-400 italic">No bank details on file</div>
                    )}
                  </div>

                  {r.status==='completed'&&<div className="px-5 py-3 border-t text-sm text-slate-600">{r.paymentReference?`Payment reference: ${r.paymentReference}`:'Historical operator record — no payment reference saved.'}<br/>Bank arrival unverified.</div>}
                  {/* Admin note (processed) */}
                  {!isPending && r.adminNote && (
                    <div className="border-t border-gray-100 px-5 py-3 text-sm text-gray-500 italic">
                      Note: {r.adminNote}
                    </div>
                  )}
                  {r.processedAt && (
                    <div className="border-t border-gray-100 px-5 py-2 text-xs text-gray-400">
                      Processed {fmtDate(r.processedAt)}
                    </div>
                  )}

                  {/* Actions for pending */}
                  {isPending && (
                    <div className="border-t border-gray-100 px-5 py-4 space-y-3">
                      <input
                        type="text"
                        placeholder="Admin note (optional)"
                        value={noteInputs[r.id] ?? ""}
                        onChange={(e) => setNoteInputs(prev => ({ ...prev, [r.id]: e.target.value }))}
                        className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#1e3a5f]/30"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => void handleAction(r.id, "approved")}
                          disabled={processingId === r.id||!!r.cashHold?.held}
                          className="flex-1 flex items-center justify-center gap-1.5 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white text-sm font-semibold py-2.5 rounded-lg transition-colors"
                        >
                          <CheckCircle size={15} /> Approve
                        </button>
                        <button
                          onClick={() => void handleAction(r.id, "rejected")}
                          disabled={processingId === r.id}
                          className="flex-1 flex items-center justify-center gap-1.5 border border-red-300 text-red-600 hover:bg-red-50 disabled:opacity-50 text-sm font-semibold py-2.5 rounded-lg transition-colors"
                        >
                          <XCircle size={15} /> Reject
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Mark as Paid for approved */}
                  {isApproved && (
                    <div className="border-t border-gray-100 px-5 py-4">
                      <p className="text-xs text-gray-500 mb-3">After sending {pence(r.amountPence)} to the account above, save its payment reference to notify the driver.</p>
                      <label className="block text-sm mb-3">Payment reference<input className="block w-full border rounded-lg p-2 mt-1" maxLength={120} value={paymentReferences[r.id]??''} onChange={e=>setPaymentReferences(p=>({...p,[r.id]:e.target.value}))}/></label>
                      <button
                        onClick={() => void handleAction(r.id, "completed")}
                        disabled={processingId === r.id||!!r.cashHold?.held}
                        className="flex items-center justify-center gap-1.5 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white text-sm font-semibold px-4 py-2.5 rounded-lg transition-colors"
                      >
                        <CheckCircle size={15} /> Record transfer sent
                      </button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </AdminLayout>
  );
}
